import React, { useEffect, useState } from 'react';
import { Button, Empty, NavBar } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import { useTonPrice, formatUsd, formatKrw } from '../../hooks/useTonPrice';
import schools from '../../db/schools.json';
import { localizeSchool } from '../../db/localizeSchool';
import { useT } from '../../i18n';
import './styles.css';

const formatDate = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString();
    } catch {
        return '';
    }
};

const formatTonAmount = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return String(n);
    return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 9 });
};

const MyDonationsShell = ({ children, navigate, title }) => (
    <div className="app">
        <div className="top">
            <NavBar onBack={() => navigate('/')}>{title}</NavBar>
        </div>
        <div className="content">
            {children}
        </div>
    </div>
);

const MyDonations = () => {
    const navigate = useNavigate();
    const { locale, t } = useT();
    const { usd: tonUsd, krw: tonKrw } = useTonPrice();
    const [records, setRecords] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const localizedSchools = schools.map((school) => localizeSchool(school, locale));
    const statusLabel = {
        confirmed: t('mydonations_status_confirmed'),
        submitted: t('mydonations_status_pending'),
        not_found: t('mydonations_status_pending'),
        mismatch: t('mydonations_status_mismatch'),
        unverified: t('mydonations_status_unverified'),
        confirmation_error: t('mydonations_status_check_failed'),
    };
    const getSchoolName = (schoolId, fallback) =>
        localizedSchools.find((school) => school.id === schoolId)?.name || fallback;

    useEffect(() => {
        const initData = (typeof window !== 'undefined' && window.Telegram?.WebApp?.initData) || '';
        if (!initData) {
            setError(t('open_via_telegram'));
            setLoading(false);
            return;
        }
        let alive = true;
        fetch('/api/donations/me', {
            headers: { 'x-telegram-init-data': initData },
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(response.status === 401 ? t('open_via_telegram_short') : t('load_failed'));
                }
                return response.json();
            })
            .then((data) => {
                if (!alive) return;
                setRecords(Array.isArray(data.records) ? data.records : []);
                setSummary(data.summary && typeof data.summary === 'object' ? data.summary : {});
            })
            .catch((err) => {
                if (alive) setError(err.message || t('load_failed'));
            })
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => {
            alive = false;
        };
    }, [t]);

    const donateAgain = (record) => {
        const params = new URLSearchParams();
        params.set('amount', String(record.amountTon || ''));
        if (record.comment) params.set('comment', String(record.comment));
        navigate(`/donate/${record.schoolId}?${params.toString()}`);
    };

    if (loading) {
        return (
            <MyDonationsShell navigate={navigate} title={t('my_donations_title')}>
                <p>{t('loading')}</p>
            </MyDonationsShell>
        );
    }

    if (error) {
        return (
            <MyDonationsShell navigate={navigate} title={t('my_donations_title')}>
                <Empty description={error} />
            </MyDonationsShell>
        );
    }

    if (!records.length) {
        return (
            <MyDonationsShell navigate={navigate} title={t('my_donations_title')}>
                <Empty description={t('no_donations')} />
                <Button
                    size="large"
                    className="primary full_width"
                    onClick={() => navigate('/')}
                >
                    {t('first_donation')}
                </Button>
            </MyDonationsShell>
        );
    }

    return (
        <MyDonationsShell navigate={navigate} title={t('my_donations_title')}>
                <section className="my_summary">
                    <h3>{t('totals_by_recipient')}</h3>
                    {Object.entries(summary).map(([schoolId, item]) => (
                        <div className="my_summary_row" key={schoolId}>
                            <strong>{getSchoolName(schoolId, item.schoolName)}</strong>
                            <span className="my_summary_amounts">
                                {formatTonAmount(item.totalTon)} TON
                                {tonUsd ? ` · ${formatUsd(item.totalTon, tonUsd)}` : ''}
                                {tonKrw ? ` · ${formatKrw(item.totalTon, tonKrw)}` : ''}
                            </span>
                            <span className="my_summary_count">
                                {item.count}{' '}
                                {item.count === 1 ? t('donations_count_one') : t('donations_count_other')}
                            </span>
                        </div>
                    ))}
                </section>
                <section className="my_history">
                    <h3>{t('history_label')}</h3>
                    {records.map((record) => (
                        <article className="my_record" key={record.id}>
                            <div className="my_record_head">
                                <strong>{getSchoolName(record.schoolId, record.schoolName)}</strong>
                                <span className={`my_status status_${record.status}`}>
                                    {statusLabel[record.status] || record.status}
                                </span>
                            </div>
                            <div className="my_record_body">
                                <div className="my_record_amount">
                                    {formatTonAmount(record.amountTon)} TON
                                    {tonUsd ? ` · ${formatUsd(record.amountTon, tonUsd)}` : ''}
                                    {tonKrw ? ` · ${formatKrw(record.amountTon, tonKrw)}` : ''}
                                </div>
                                <div className="my_record_date">{formatDate(record.recordedAt)}</div>
                                {record.comment && <p className="my_record_comment">{record.comment}</p>}
                            </div>
                            <Button size="mini" onClick={() => donateAgain(record)}>
                                {t('donate_again')}
                            </Button>
                        </article>
                    ))}
                </section>
        </MyDonationsShell>
    );
};

export default MyDonations;
